// ===== World 10: Spaghetti land – STRINGS, PENDULUMS AND BRIDGES =====
// A tomato on a spaghetti string swings and pushes the meatball; scissors cut a string so what hangs in it falls or flies off;
// a hanging bridge of rigatoni on spaghetti carries the meatball across a gap.
// Ball → goal: meatball → plate of spaghetti with sauce (or a hanging pot lid that says "ding").
//
// Own parts: spaghetti_pendulum (short string with a tomato), spaghetti_long_pendulum (long string),
//            spaghetti_scissors (cuts strings), spaghetti_bridge (hanging bridge of rigatoni on spaghetti).
// Own objects: spaghetti_hook (hook the pendulum hangs from), spaghetti_string (the ball hangs from a string).
import type Matter from 'matter-js';
import { defineWorld, definePart, defineObj } from '../../core/registry';
import type { Ctx2D, Gfx, GameBody, IntroFn, Level, ObjData, PartDef, Placement, Reskin, SimState, CorePartType, SynthStyle, Vec, WorldDef, WorldMeta, SoundFn } from '../../core/types';
import { scene } from './scene';
import { levels } from './levels';
import { strings } from './strings';
import { K, DEG, type StringObj } from './shared';

// ---------- constants ----------
const INK = '#3B2A5C';
const PASTA1 = '#FFE58A', PASTA2 = '#F2B93B', PASTA3 = '#D9962A';
const SAUCE1 = '#FF6B4F', SAUCE2 = '#D8392B';
const BRIDGE_W = 300, BRIDGE_N = 11, BRIDGE_SAG = 16;   // bridge length, number of rigatoni pieces, sag in the middle

let curLevel: Level | null = null;      // set in setup – used to show the hook snap in the build phase

// ---------- strings (cuttable) and small helpers ----------
/** A string the scissors can cut: from point a to end() (the body hanging in it) */
interface Strand { c: Matter.Constraint; cut: boolean; cutT: number; a: Vec; end: () => Vec; body: GameBody; doCut(): void }
declare module '../../core/types' { interface SimState { spag?: { strings: Strand[] } } }

function strands(S: SimState) { S.spag = S.spag || { strings: [] }; return S.spag.strings; }
function segDist(p: Vec, a: Vec, b: Vec) {
  const dx = b.x - a.x, dy = b.y - a.y, l2 = dx * dx + dy * dy || 1;
  const k = Math.max(0, Math.min(1, ((p.x - a.x) * dx + (p.y - a.y) * dy) / l2));
  return Math.hypot(p.x - a.x - k * dx, p.y - a.y - k * dy);
}
// Where does the pendulum hang? If the level has hooks, it snaps onto the nearest hook.
function hookFor(level: Level | null, pl: Vec): Vec {
  let best: { d: number; x: number; y: number } | null = null;
  for (const lo of (level && level.objs) || []) if (lo.t === 'spaghetti_hook') {
    const o = lo as ObjData, d = Math.hypot(o.x - pl.x, o.y - pl.y);
    if (!best || d < best.d) best = { d, x: o.x, y: o.y };
  }
  return best ? { x: best.x, y: best.y } : { x: pl.x, y: pl.y };
}
// The bridge's resting shape: BRIDGE_N + 1 points along a slight sag
function bridgePoints(pl: { x: number; y: number; a?: number }) {
  const a = (pl.a || 0) * DEG, c = Math.cos(a), s = Math.sin(a);
  const p0 = { x: pl.x - c * BRIDGE_W / 2, y: pl.y - s * BRIDGE_W / 2 }, nx = -s, ny = c;
  const pts: Vec[] = [];
  for (let i = 0; i <= BRIDGE_N; i++) {
    const k = i / BRIDGE_N, d = 4 * BRIDGE_SAG * k * (1 - k);
    pts.push({ x: p0.x + c * BRIDGE_W * k + nx * d, y: p0.y + s * BRIDGE_W * k + ny * d });
  }
  return pts;
}

// ---------- drawing helpers ----------
// A spaghetti strand: soft wavy yellow line
function noodle(ctx: Ctx2D, x0: number, y0: number, x1: number, y1: number, t: number, wig?: number | null, w?: number) {
  const dx = x1 - x0, dy = y1 - y0, L = Math.hypot(dx, dy) || 1, nx = -dy / L, ny = dx / L;
  const n = Math.max(4, Math.round(L / 14));
  const path = () => {
    ctx.beginPath(); ctx.moveTo(x0, y0);
    for (let i = 1; i <= n; i++) {
      const k = i / n, o = i === n ? 0 : Math.sin(k * 11 + (t || 0) * 0.08) * (wig == null ? 2.2 : wig) * Math.sin(k * Math.PI);
      ctx.lineTo(x0 + dx * k + nx * o, y0 + dy * k + ny * o);
    }
  };
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  path(); ctx.strokeStyle = INK; ctx.lineWidth = (w || 6) + 3; ctx.stroke();
  path(); ctx.strokeStyle = PASTA2; ctx.lineWidth = w || 6; ctx.stroke();
  path(); ctx.strokeStyle = PASTA1; ctx.lineWidth = (w || 6) * 0.45; ctx.stroke();
}
function hook(ctx: Ctx2D, x: number, y: number) {
  ctx.save(); ctx.translate(x, y);
  ctx.fillStyle = '#B9BFCF'; ctx.strokeStyle = INK; ctx.lineWidth = 3;
  ctx.beginPath(); ctx.arc(0, -12, 9, 0, 7); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.arc(0, -12, 3, 0, 7); ctx.fillStyle = INK; ctx.fill();
  ctx.lineCap = 'round';
  ctx.strokeStyle = INK; ctx.lineWidth = 7; ctx.beginPath(); ctx.moveTo(0, -4); ctx.lineTo(0, 4); ctx.arc(-6, 4, 6, 0, Math.PI * 0.95); ctx.stroke();
  ctx.strokeStyle = '#DCE0EA'; ctx.lineWidth = 3.5; ctx.beginPath(); ctx.moveTo(0, -4); ctx.lineTo(0, 4); ctx.arc(-6, 4, 6, 0, Math.PI * 0.95); ctx.stroke();
  ctx.restore();
}
function tomato(ctx: Ctx2D, x: number, y: number, r: number, rot: number, g: Gfx, sel?: boolean) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(rot || 0);
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .15, 0, 0, r * 1.05);
  gr.addColorStop(0, '#FFB3A0'); gr.addColorStop(0.45, SAUCE1); gr.addColorStop(1, SAUCE2);
  ctx.beginPath(); ctx.ellipse(0, 0, r * 1.06, r, 0, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = sel ? '#FFC93C' : INK; if (sel) ctx.lineWidth = 5; ctx.stroke();
  ctx.fillStyle = '#4DB85A'; ctx.strokeStyle = INK; ctx.lineWidth = 2;
  ctx.beginPath();
  for (let i = 0; i < 5; i++) { const a = -Math.PI / 2 + i * 2 * Math.PI / 5; ctx.lineTo(Math.cos(a) * r * .5, -r * .78 + Math.sin(a) * r * .3); ctx.lineTo(Math.cos(a + .6) * r * .15, -r * .8 + Math.sin(a + .6) * r * .1); }
  ctx.closePath(); ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.ellipse(-r * .4, -r * .3, r * .22, r * .13, -0.6, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.6)'; ctx.fill();
  ctx.restore();
}
function scissorsDraw(ctx: Ctx2D, x: number, y: number, closed: boolean, g: Gfx, sel: boolean, s?: number) {
  ctx.save(); ctx.translate(x, y); ctx.scale(s || 1, s || 1);
  const o = closed ? 0.04 : 0.42;
  if (sel) { ctx.beginPath(); ctx.arc(0, 0, 34, 0, 7); ctx.strokeStyle = '#FFC93C'; ctx.lineWidth = 5; ctx.stroke(); }
  g.shadowOn(ctx, 6, 3);
  for (const s2 of [-1, 1]) {
    ctx.save(); ctx.rotate(s2 * o - Math.PI / 2 - 0.35);
    // blade
    ctx.beginPath(); ctx.moveTo(0, -3); ctx.lineTo(30, s2 * 1); ctx.lineTo(0, 4); ctx.closePath();
    ctx.fillStyle = '#E6E9F2'; ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2.5; ctx.stroke();
    // handle
    ctx.beginPath(); ctx.ellipse(-16, s2 * 3, 10, 7, 0, 0, 7); ctx.lineWidth = 6; ctx.strokeStyle = INK; ctx.stroke();
    ctx.lineWidth = 3.5; ctx.strokeStyle = '#FF5A5F'; ctx.stroke();
    ctx.restore();
  }
  g.shadowOff(ctx);
  ctx.beginPath(); ctx.arc(0, 0, 3.5, 0, 7); ctx.fillStyle = INK; ctx.fill();
  ctx.restore();
}

// Hanging bridge: pts = points along the bridge, segs = the rigatoni pieces (centre, angle, length)
interface Seg { x: number; y: number; a: number; w: number }
function drawBridge(ctx: Ctx2D, pts: Vec[], segs: Seg[], g: Gfx, sel: boolean) {
  const n = pts.length;
  // posts at the ends
  for (const p of [pts[0], pts[n - 1]]) {
    ctx.save(); ctx.translate(p.x, p.y);
    ctx.fillStyle = '#C98A4B'; ctx.strokeStyle = INK; ctx.lineWidth = 3;
    g.rr(ctx, -6, -30, 12, 36, 5); ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.arc(0, -32, 8, 0, 7); ctx.fillStyle = '#FF6B4F'; ctx.fill(); ctx.stroke();
    ctx.restore();
  }
  // handrail strings (spaghetti) from the post tops along the bridge
  const top0 = { x: pts[0].x, y: pts[0].y - 30 }, top1 = { x: pts[n - 1].x, y: pts[n - 1].y - 30 };
  ctx.save();
  const rail = () => { ctx.beginPath(); ctx.moveTo(top0.x, top0.y); for (let i = 1; i < n - 1; i++) ctx.lineTo(pts[i].x, pts[i].y - 22); ctx.lineTo(top1.x, top1.y); };
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  rail(); ctx.strokeStyle = INK; ctx.lineWidth = 6; ctx.stroke(); rail(); ctx.strokeStyle = PASTA2; ctx.lineWidth = 3.5; ctx.stroke();
  for (let i = 1; i < n - 1; i++) { ctx.beginPath(); ctx.moveTo(pts[i].x, pts[i].y - 22); ctx.lineTo(pts[i].x, pts[i].y); ctx.strokeStyle = PASTA2; ctx.lineWidth = 3; ctx.stroke(); }
  ctx.restore();
  // the load-bearing spaghetti
  const line = () => { ctx.beginPath(); ctx.moveTo(pts[0].x, pts[0].y); for (let i = 1; i < n; i++) ctx.lineTo(pts[i].x, pts[i].y); };
  ctx.lineCap = 'round'; ctx.lineJoin = 'round';
  line(); ctx.strokeStyle = INK; ctx.lineWidth = 9; ctx.stroke(); line(); ctx.strokeStyle = PASTA2; ctx.lineWidth = 6; ctx.stroke();
  // rigatoni pieces
  for (let i = 0; i < segs.length; i++) {
    const s = segs[i];
    ctx.save(); ctx.translate(s.x, s.y); ctx.rotate(s.a);
    const w = s.w - 5;
    g.rr(ctx, -w / 2, -8, w, 16, 6); ctx.fillStyle = i % 2 ? PASTA1 : '#FFD86B'; ctx.fill();
    ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
    ctx.strokeStyle = 'rgba(217,150,42,.8)'; ctx.lineWidth = 2;
    for (let k = -1; k <= 1; k++) { ctx.beginPath(); ctx.moveTo(k * w / 4, -6); ctx.lineTo(k * w / 4, 6); ctx.stroke(); }
    ctx.restore();
  }
  if (sel) { ctx.save(); line(); ctx.strokeStyle = 'rgba(255,201,60,.55)'; ctx.lineWidth = 30; ctx.stroke(); ctx.restore(); }
}
// The bridge at rest (build phase and toolbox icon)
function drawBridgePlacement(ctx: Ctx2D, pl: { x: number; y: number; a?: number }, sel: boolean, g: Gfx) {
  const pts = bridgePoints(pl), segs: Seg[] = [];
  for (let i = 0; i < pts.length - 1; i++) segs.push({ x: (pts[i].x + pts[i + 1].x) / 2, y: (pts[i].y + pts[i + 1].y) / 2, a: Math.atan2(pts[i + 1].y - pts[i].y, pts[i + 1].x - pts[i].x), w: Math.hypot(pts[i + 1].x - pts[i].x, pts[i + 1].y - pts[i].y) });
  drawBridge(ctx, pts, segs, g, sel);
}

// Kit parts in spaghetti style: plank = lasagne sheet, pad = ravioli, banana = macaroni, stone = parmesan, seesaw = wooden spoon
function lasagne(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  g.shadowOn(ctx, 10, 5);
  const path = () => {
    ctx.beginPath(); ctx.moveTo(-w / 2, -h / 2);
    const n = Math.round(w / 14);
    for (let i = 0; i <= n; i++) ctx.lineTo(-w / 2 + i * w / n, -h / 2 + (i % 2 ? -3 : 1));
    ctx.lineTo(w / 2, h / 2);
    for (let i = n; i >= 0; i--) ctx.lineTo(-w / 2 + i * w / n, h / 2 + (i % 2 ? 3 : -1));
    ctx.closePath();
  };
  path(); ctx.fillStyle = g.lg(ctx, -h / 2, h / 2, PASTA1, PASTA2); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
  ctx.strokeStyle = 'rgba(217,150,42,.7)'; ctx.lineWidth = 2; ctx.beginPath(); ctx.moveTo(-w / 2 + 8, 0); ctx.lineTo(w / 2 - 8, 0); ctx.stroke();
  if (sel) { path(); ctx.lineWidth = 5; ctx.strokeStyle = '#FFC93C'; ctx.stroke(); }
  ctx.restore();
}
function ravioli(ctx: Ctx2D, x: number, y: number, a: number, w: number, h: number, sel: boolean, g: Gfx) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a);
  g.shadowOn(ctx, 10, 5);
  g.rr(ctx, -w / 2, -h / 2 - 4, w, h + 8, 8); ctx.fillStyle = g.lg(ctx, -h / 2, h / 2, '#FFE9A0', PASTA2); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = sel ? '#FFC93C' : INK; ctx.stroke();
  ctx.fillStyle = PASTA3;
  for (let i = -w / 2 + 6; i < w / 2 - 2; i += 8) { ctx.beginPath(); ctx.arc(i, -h / 2 - 1, 1.8, 0, 7); ctx.fill(); ctx.beginPath(); ctx.arc(i, h / 2 + 1, 1.8, 0, 7); ctx.fill(); }
  ctx.beginPath(); ctx.ellipse(0, 0, w * .32, h * .45, 0, 0, 7); ctx.fillStyle = '#FFD24A'; ctx.fill(); ctx.strokeStyle = 'rgba(200,130,30,.7)'; ctx.lineWidth = 2; ctx.stroke();
  ctx.fillStyle = SAUCE1; ctx.beginPath(); ctx.arc(0, -1, 3, 0, 7); ctx.fill();
  ctx.restore();
}
function macaroni(ctx: Ctx2D, pl: { x: number; y: number; a?: number }, sel: boolean, g: Gfx) {
  const r = 95, a = (pl.a || 0) * DEG;
  ctx.save(); ctx.translate(pl.x, pl.y); ctx.rotate(a); ctx.translate(0, -r * 0.55);
  const arc = (rr: number) => { ctx.beginPath(); ctx.arc(0, 0, rr, 20 * DEG, 160 * DEG); };
  g.shadowOn(ctx, 10, 5);
  ctx.lineCap = 'round';
  arc(r); ctx.strokeStyle = INK; ctx.lineWidth = 26; ctx.stroke(); g.shadowOff(ctx);
  arc(r); ctx.strokeStyle = PASTA2; ctx.lineWidth = 20; ctx.stroke();
  arc(r - 3); ctx.strokeStyle = PASTA1; ctx.lineWidth = 9; ctx.stroke();
  ctx.strokeStyle = 'rgba(200,130,30,.6)'; ctx.lineWidth = 2;
  for (let k = 28; k < 160; k += 12) { const c = Math.cos(k * DEG), s = Math.sin(k * DEG); ctx.beginPath(); ctx.moveTo(c * (r - 9), s * (r - 9)); ctx.lineTo(c * (r + 9), s * (r + 9)); ctx.stroke(); }
  // the hollow ends
  for (const k of [20, 160]) { const c = Math.cos(k * DEG), s = Math.sin(k * DEG); ctx.beginPath(); ctx.ellipse(c * r, s * r, 11, 6, k * DEG, 0, 7); ctx.fillStyle = PASTA3; ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 2.5; ctx.stroke(); }
  if (sel) { arc(r); ctx.strokeStyle = 'rgba(255,201,60,.6)'; ctx.lineWidth = 36; ctx.stroke(); }
  ctx.restore();
}
function parmesan(ctx: Ctx2D, x: number, y: number, a: number | undefined, sel: boolean, g: Gfx) {
  ctx.save(); ctx.translate(x, y); ctx.rotate(a || 0);
  g.shadowOn(ctx, 10, 6);
  ctx.beginPath(); ctx.moveTo(-26, 18); ctx.lineTo(26, 18); ctx.lineTo(22, -8); ctx.quadraticCurveTo(0, -30, -22, -14); ctx.closePath();
  ctx.fillStyle = g.lg(ctx, -24, 18, '#FFF6C8', '#EBC45A'); ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 3; ctx.strokeStyle = sel ? '#FFC93C' : INK; ctx.stroke();
  ctx.fillStyle = 'rgba(200,150,40,.55)';
  [[-10, 4, 4], [8, -4, 3], [12, 10, 3.5], [-2, -10, 2.5]].forEach(([px, py, r]) => { ctx.beginPath(); ctx.arc(px, py, r, 0, 7); ctx.fill(); });
  ctx.restore();
}
function spoonSeesaw(ctx: Ctx2D, pl: Placement, g: Gfx) {
  const a = (pl.a || 0) * DEG;
  ctx.save(); ctx.translate(pl.x, pl.y);
  ctx.beginPath(); ctx.moveTo(-16, 20); ctx.lineTo(0, 0); ctx.lineTo(16, 20); ctx.closePath(); ctx.fillStyle = '#C98A4B'; ctx.fill(); ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.stroke();
  ctx.rotate(a);
  g.shadowOn(ctx, 8, 4);
  g.rr(ctx, -115, -7, 150, 14, 7); ctx.fillStyle = '#E3A565'; ctx.fill(); g.shadowOff(ctx); ctx.stroke();
  ctx.beginPath(); ctx.ellipse(78, -2, 40, 13, 0, 0, 7); ctx.fillStyle = '#E3A565'; ctx.fill(); ctx.stroke();
  ctx.beginPath(); ctx.ellipse(78, -5, 30, 7, 0, 0, 7); ctx.fillStyle = '#C98A4B'; ctx.fill();
  ctx.beginPath(); ctx.arc(0, 0, 5, 0, 7); ctx.fillStyle = INK; ctx.fill();
  ctx.restore();
}

// Intro animation: the tomato swings down and pushes the meatball along the shelf
const introPendulum: IntroFn = (ctx, t, g, h) => {
  const ax = 170, ay = 40, LL = 130, ph = (t % 240) / 240;
  const a = ph < 0.23 ? 60 * Math.cos(ph / 0.23 * Math.PI / 2) : ph < 0.5 ? -20 * Math.sin((ph - 0.23) / 0.27 * Math.PI) : 0;
  const bx = ax - Math.sin(a * DEG) * LL, by = ay + Math.cos(a * DEG) * LL;
  g.block(ctx, 300, 214, 380, 20, 0, '#FFE58A', '#D9962A', 9);
  hook(ctx, ax, ay); noodle(ctx, ax, ay + 6, bx, by, t);
  tomato(ctx, bx, by, 22, 0, g);
  const bx2 = ph < 0.23 ? 215 : Math.min(420, 215 + (ph - 0.23) * 900);
  g.ball(ctx, h.fakeBall(bx2, 189, ph < 0.23 ? 0 : 5, 0, 15), 'hero', t);
};
// Intro animation: the meatball swings on its string, the scissors cut it and it drops onto the table
const introScissors: IntroFn = (ctx, t, g, h) => {
  const ph = (t % 240) / 240, ax = 230, ay = 30, LL = 120;
  const cut = ph > 0.35;
  const a = cut ? 0 : 30 * Math.cos(ph / 0.35 * Math.PI * 2);
  let bx = ax - Math.sin(a * DEG) * LL, by = ay + Math.cos(a * DEG) * LL;
  if (cut) { const k = (ph - 0.35) * 240; by = Math.min(ay + LL + k * k * 0.03, 236); }
  g.block(ctx, 230, 262, 200, 20, 0, '#FFFFFF', '#F1D9D0', 9);
  hook(ctx, ax, ay);
  if (!cut) noodle(ctx, ax, ay + 6, bx, by - 15, t); else noodle(ctx, ax, ay + 6, ax + 4, ay + 45, t, 3);
  scissorsDraw(ctx, 262, 95, cut, g, false);
  g.ball(ctx, h.fakeBall(bx, by, 0, cut ? 3 : 0, 15), 'hero', t);
};
// Intro animation: the meatball rolls across the bridge, which dips under it
const introBridge: IntroFn = (ctx, t, g, h) => {
  const ph = (t % 240) / 240, x = 60 + ph * 380;
  g.block(ctx, 50, 170, 100, 20, 0, '#FFE58A', '#D9962A', 9); g.block(ctx, 410, 170, 100, 20, 0, '#FFE58A', '#D9962A', 9);
  const pts = bridgePoints({ x: 230, y: 162, a: 0 }).map(p => {
    const dip = x > 80 && x < 380 ? 30 * Math.exp(-Math.pow((p.x - x) / 70, 2)) : 0; return { x: p.x, y: p.y + dip };
  });
  const segs: Seg[] = []; for (let i = 0; i < pts.length - 1; i++) segs.push({ x: (pts[i].x + pts[i + 1].x) / 2, y: (pts[i].y + pts[i + 1].y) / 2, a: Math.atan2(pts[i + 1].y - pts[i].y, pts[i + 1].x - pts[i].x), w: 28 });
  drawBridge(ctx, pts, segs, g, false);
  let by = 145; for (let i = 0; i < pts.length - 1; i++) if (x >= pts[i].x && x <= pts[i + 1].x) by = pts[i].y + (pts[i + 1].y - pts[i].y) * (x - pts[i].x) / (pts[i + 1].x - pts[i].x) - 22;
  if (x < 80 || x > 380) by = 145;
  g.ball(ctx, h.fakeBall(x, by, 4, 0, 15), 'hero', t);
};

// ---------- skins ----------
const skins: WorldDef['skins'] = {
  // a bundle of dry spaghetti (shelves)
  spaghetti_pasta(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, PASTA1, PASTA2, Math.min(9, h / 2));
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    ctx.strokeStyle = 'rgba(200,130,30,.55)'; ctx.lineWidth = 1.6;
    for (let k = -h / 2 + 5; k < h / 2 - 2; k += 5) { ctx.beginPath(); ctx.moveTo(-w / 2 + 8, k); ctx.lineTo(w / 2 - 8, k); ctx.stroke(); }
    // bands round the bundle
    if (w > 120) for (const bx of [-w / 2 + 40, w / 2 - 40]) { ctx.fillStyle = '#4DA3FF'; ctx.fillRect(bx - 5, -h / 2, 10, h); ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.strokeRect(bx - 5, -h / 2, 10, h); }
    ctx.restore();
  },
  // checked tablecloth (the table under the plate)
  spaghetti_tablecloth(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, '#FFFFFF', '#F1D9D0', Math.min(10, h / 2));
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    g.rr(ctx, -w / 2 + 2, -h / 2 + 2, w - 4, h - 4, Math.min(8, h / 2 - 2)); ctx.clip();
    ctx.fillStyle = 'rgba(232,70,60,.55)';
    const q = 14;
    for (let i = -Math.ceil(w / q); i < w / q; i++) for (let j = -Math.ceil(h / q); j < h / q; j++) if ((i + j) % 2 === 0) ctx.fillRect(i * q, j * q, q, q);
    ctx.restore();
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    g.rr(ctx, -w / 2, -h / 2, w, h, Math.min(10, h / 2)); ctx.lineWidth = 3; ctx.strokeStyle = 'rgba(59,42,92,.85)'; ctx.stroke();
    ctx.restore();
  },
  // a block of cheese with holes
  spaghetti_cheese(ctx: Ctx2D, b: GameBody, g: Gfx) {
    const { x, y } = b.position, w = b.dw!, h = b.dh!;
    g.block(ctx, x, y, w, h, b.angle, '#FFF3B8', '#F0CF62', Math.min(12, w / 2));
    ctx.save(); ctx.translate(x, y); ctx.rotate(b.angle);
    ctx.fillStyle = 'rgba(214,165,50,.55)';
    const n = Math.max(1, Math.round(w * h / 2600));
    for (let i = 0; i < n; i++) {
      const px = ((i * 37) % 100) / 100 - .5, py = ((i * 61 + 23) % 100) / 100 - .5;
      ctx.beginPath(); ctx.arc(px * (w - 14), py * (h - 14), Math.min(6, w / 5), 0, 7); ctx.fill();
    }
    ctx.restore();
  },
};

// ---------- parts ----------
// Pendulum: a tomato on a spaghetti string of length L (radius R). The angle a is how far it is pulled back.
function pendulum(L: number, R: number): PartDef {
  const bobAt = (ax: number, ay: number, a: number) => ({ x: ax - Math.sin(a * DEG) * L, y: ay + Math.cos(a * DEG) * L });
  return {
    rects: [{ x: 0, y: 0, w: 30, h: 24 }, { x: 0, y: L / 2, w: 12, h: L - 30 }, { x: 0, y: L, w: R * 2 + 6, h: R * 2 + 6 }],
    rot: 30, minA: -90, maxA: 90, defaultA: 60,
    draw(ctx, pl, sel, g) {
      const h = hookFor(curLevel, pl), b = bobAt(h.x, h.y, pl.a || 0);
      // arc: where the tomato will swing
      if (pl.a) {
        ctx.save(); ctx.setLineDash([3, 11]); ctx.lineCap = 'round'; ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(59,42,92,.28)';
        const a0 = Math.PI / 2 + pl.a * DEG, a1 = Math.PI / 2 - pl.a * DEG;
        ctx.beginPath(); ctx.arc(h.x, h.y, L, Math.min(a0, a1), Math.max(a0, a1)); ctx.stroke(); ctx.restore();
      }
      hook(ctx, h.x, h.y);
      noodle(ctx, h.x, h.y + 6, b.x, b.y, 0);
      tomato(ctx, b.x, b.y, R, 0, g, sel);
    },
    icon(ctx, g) {
      hook(ctx, 0, -40);
      noodle(ctx, 0, -34, -26, 18, 0, 1.5, 5);
      tomato(ctx, -28, 22, 20, 0, g);
    },
    create(api, pl) {
      const h = hookFor(api.level, pl), b0 = bobAt(h.x, h.y, pl.a || 0);
      const b = api.add(api.Bodies.circle(b0.x, b0.y, R, { density: 0.006, friction: 0.05, frictionStatic: 0, restitution: 0.3, frictionAir: 0.0015, label: K + '_tomato' }));
      b.kind = K + '_tomato'; api.dynamic(b);
      const c = api.Constraint.create({ pointA: { x: h.x, y: h.y + 6 }, bodyB: b, pointB: { x: 0, y: 0 }, length: L - 6, stiffness: 1, damping: 0 });
      api.Composite.add(api.world, c);
      const st: Strand = { c, cut: false, cutT: 0, a: { x: h.x, y: h.y + 6 }, end: () => b.position, body: b,
        doCut() { this.cut = true; this.cutT = api.S.t; api.Composite.remove(api.world, c); } };
      strands(api.S).push(st);
      let lastHit = -99;
      api.onHit(b, o => { if (api.isBall(o) && api.S.t - lastHit > 20 && b.speed > 1.5) { lastHit = api.S.t; api.sfx('spaghetti_bonk', o.position.x, o.position.y, '#FF6B4F', 6); } });
      return {
        layer: 'mid',
        draw(ctx, t, g, S) {
          hook(ctx, h.x, h.y);
          if (!st.cut) noodle(ctx, h.x, h.y + 6, b.position.x, b.position.y, t);
          else { const k = Math.min(1, (S.t - st.cutT) / 15); noodle(ctx, h.x, h.y + 6, h.x + 6 * k, h.y + 40 - 10 * k, t, 3); }
          tomato(ctx, b.position.x, b.position.y, R, b.angle * 0.3, g);
        },
      };
    },
    intro: introPendulum,
  };
}

/** A bridge piece (body) remembers its length for drawing */
type BridgeSeg = GameBody & { len: number };

const parts = {
  spaghetti_pendulum: definePart(pendulum(150, 24)),
  spaghetti_long_pendulum: definePart(pendulum(270, 24)),

  // Scissors: cut the first string (own strand or kit rope) that passes within 30 px
  spaghetti_scissors: definePart({
    circle: 26, rot: false,
    draw(ctx, pl, sel, g) { scissorsDraw(ctx, pl.x, pl.y, false, g, sel); },
    icon(ctx, g) { scissorsDraw(ctx, 0, 0, false, g, false, 1.5); },
    create(api, pl) {
      const me = { closed: -1 };
      return {
        layer: 'front',
        step() {
          const S = api.S;
          if (me.closed >= 0) return;
          for (const s of strands(S)) {
            if (s.cut) continue;
            if (segDist(pl, s.a, s.end()) < 30) {
              s.doCut(); me.closed = S.t;
              api.sfx('spaghetti_snip', pl.x, pl.y, PASTA2, 8);
              return;
            }
          }
          for (const r of S.ropes) if (!r.cut && segDist(pl, { x: r.ax, y: r.ay }, r.body.position) < 30) { api.act({ cut: r.id }); me.closed = S.t; return; }
        },
        draw(ctx, t, g, S) {
          const k = me.closed >= 0 ? Math.min(1, (S.t - me.closed) / 6) : 0;
          scissorsDraw(ctx, pl.x, pl.y, k > 0.5, g, false);
        },
      };
    },
    intro: introScissors,
  }),

  // Hanging bridge: BRIDGE_N rigatoni pieces pinned to each other and to both ends
  spaghetti_bridge: definePart({
    rects: [{ x: 0, y: 0, w: BRIDGE_W, h: 16 }], rot: 15, minA: -30, maxA: 30, snap: true, defaultA: 0,
    draw(ctx, pl, sel, g) { drawBridgePlacement(ctx, pl, sel, g); },
    icon(ctx, g) {
      ctx.scale(.33, .33);
      drawBridgePlacement(ctx, { x: 0, y: 20, a: 0 }, false, g);
    },
    create(api, pl) {
      const { Constraint, Composite } = api;
      const pts = bridgePoints(pl), segs: BridgeSeg[] = [];
      const grp = api.Body.nextGroup(true);
      for (let i = 0; i < pts.length - 1; i++) {
        const p = pts[i], q = pts[i + 1], len = Math.hypot(q.x - p.x, q.y - p.y);
        const b = api.add(api.R((p.x + q.x) / 2, (p.y + q.y) / 2, len + 3, 12, {
          angle: Math.atan2(q.y - p.y, q.x - p.x), density: 0.0012, friction: 0.2, restitution: 0.05, frictionAir: 0.03,
          collisionFilter: { group: grp }, chamfer: { radius: 5 }, label: K + '_bridge',
        })) as BridgeSeg;
        b.kind = K + '_bridge'; b.len = len; api.dynamic(b); segs.push(b);
      }
      const pin = (p: Vec, b: GameBody) => { const c = Constraint.create({ pointA: { x: p.x, y: p.y }, bodyB: b, pointB: { x: p.x - b.position.x, y: p.y - b.position.y }, length: 0, stiffness: 0.95, damping: 0.1 }); Composite.add(api.world, c); };
      pin(pts[0], segs[0]); pin(pts[pts.length - 1], segs[segs.length - 1]);
      for (let i = 0; i < segs.length - 1; i++) {
        const A = segs[i], B = segs[i + 1], p = pts[i + 1];
        const c = Constraint.create({ bodyA: A, pointA: { x: p.x - A.position.x, y: p.y - A.position.y }, bodyB: B, pointB: { x: p.x - B.position.x, y: p.y - B.position.y }, length: 0, stiffness: 0.95, damping: 0.1 });
        Composite.add(api.world, c);
      }
      let lastT = -99;
      for (const b of segs) api.onHit(b, o => { if (api.isBall(o) && api.S.t - lastT > 30 && o.speed > 2) { lastT = api.S.t; api.sfx('spaghetti_twang', o.position.x, o.position.y); } });
      return {
        layer: 'mid',
        draw(ctx, t, g) {
          // joint points: midway between the ends of neighbouring pieces
          const P = [pts[0]];
          for (let i = 0; i < segs.length; i++) {
            const b = segs[i], c = Math.cos(b.angle), s = Math.sin(b.angle), h = b.len / 2;
            if (i < segs.length - 1) { const B = segs[i + 1], c2 = Math.cos(B.angle), s2 = Math.sin(B.angle), h2 = B.len / 2; P.push({ x: (b.position.x + c * h + B.position.x - c2 * h2) / 2, y: (b.position.y + s * h + B.position.y - s2 * h2) / 2 }); }
          }
          P.push(pts[pts.length - 1]);
          drawBridge(ctx, P, segs.map(b => ({ x: b.position.x, y: b.position.y, a: b.angle, w: b.len })), g, false);
        },
      };
    },
    intro: introBridge,
  }),
};

// ---------- objects ----------
interface StringState { s: Strand; b: GameBody }
const objs = {
  // A hook on the wall – pendulums snap onto it
  spaghetti_hook: defineObj<object>({
    layer: 'back',
    create() { return {}; },
    draw(ctx, st, t) {
      // Always drawn, with a pulsing ring; a pendulum hanging on it draws its own hook on top.
      hook(ctx, st.o.x, st.o.y);
      ctx.save(); ctx.globalAlpha = 0.5 + 0.2 * Math.sin(t * 0.08);
      ctx.beginPath(); ctx.arc(st.o.x, st.o.y - 12, 16, 0, 7); ctx.strokeStyle = '#FFFFFF'; ctx.lineWidth = 3; ctx.setLineDash([4, 5]); ctx.stroke();
      ctx.restore();
    },
  }),
  // The ball (or a helper) hangs from a hook on a spaghetti string
  spaghetti_string: defineObj<StringState>({
    layer: 'mid',
    create(api, od) {
      const o = od as StringObj;
      let b: GameBody;
      if (o.cheese) {               // a lump of parmesan hangs from the string (heavy, falls when the string is cut)
        b = api.add(api.Bodies.circle(o.x, o.y, 24, { density: 0.015, friction: 0.3, restitution: 0.1, frictionAir: 0.001 }));
        b.kind = 'stone'; api.dynamic(b);
      } else { const want = api.level.balls![o.ball || 0]; b = api.S.dyn.find(x => x.def === want)!; }
      const top = { x: o.ax, y: o.ay + 6 };
      const L = Math.hypot(b.position.x - top.x, b.position.y - top.y);
      const c = api.Constraint.create({ pointA: top, bodyB: b, pointB: { x: 0, y: 0 }, length: L, stiffness: 1, damping: 0 });
      api.Composite.add(api.world, c);
      const st: Strand = { c, cut: false, cutT: 0, a: top, end: () => b.position, body: b,
        doCut() { this.cut = true; this.cutT = api.S.t; api.Composite.remove(api.world, c); } };
      strands(api.S).push(st);
      return { s: st, b };
    },
    blockers(od) { const o = od as StringObj; return [{ x: o.ax, y: o.ay, r: 22 }].concat(o.cheese ? [{ x: o.x, y: o.y, r: 30 }] : []); },
    draw(ctx, st, t, g, S) {
      const o = st.o as StringObj, s = st.s, b = st.b;
      hook(ctx, o.ax, o.ay);
      if (!s.cut) {
        const dx = b.position.x - o.ax, dy = b.position.y - o.ay - 6, L = Math.hypot(dx, dy) || 1;
        noodle(ctx, o.ax, o.ay + 6, b.position.x - dx / L * b.circleRadius! * .7, b.position.y - dy / L * b.circleRadius! * .7, t);
      } else {
        const k = Math.min(1, (S.t - s.cutT) / 15);
        noodle(ctx, o.ax, o.ay + 6, o.ax + 6 * k, o.ay + 46 - 10 * k, t, 3);
      }
    },
  }),
};

// ---------- sounds ----------
const sounds: Record<string, SoundFn> = {
  spaghetti_bonk: (tone, noise) => { tone(180, 0.18, 'sine', 0.35, 90); noise(0.08, 0.15, 900, 1); },
  spaghetti_snip: (tone, noise) => { noise(0.05, 0.3, 4000, 3); noise(0.05, 0.25, 5000, 3, 0.07); tone(900, 0.08, 'square', 0.06, 700); },
  spaghetti_twang: (tone) => { tone(140, 0.35, 'triangle', 0.25, 95); tone(210, 0.3, 'sine', 0.12, 150, 0.02); },
};

// ---------- reskins (kit parts, balls and goals in spaghetti style) ----------
const reskin: Partial<Record<CorePartType, Reskin>> = {
  plank(ctx, pl, sel, g, rects) { const r = rects[0]; lasagne(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
  long_plank(ctx, pl, sel, g, rects) { const r = rects[0]; lasagne(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
  pad(ctx, pl, sel, g, rects) { const r = rects[0]; ravioli(ctx, r.x, r.y, r.a, r.w, r.h, sel, g); },
  banana(ctx, pl, sel, g) { macaroni(ctx, pl, sel, g); },
  // the stone's placement carries its body once the level runs (so the parmesan turns with it)
  stone(ctx, pl, sel, g) { const body = pl.body as GameBody | undefined; parmesan(ctx, pl.x, pl.y, body ? body.angle : 0, sel, g); },
  seesaw(ctx, pl, sel, g) { spoonSeesaw(ctx, pl, g); },
};
const icons: WorldDef['icons'] = {
  plank(ctx, g) { lasagne(ctx, 0, 0, -0.3, 96, 18, false, g); },
  long_plank(ctx, g) { lasagne(ctx, 0, 0, -0.5, 108, 16, false, g); },
  pad(ctx, g) { ravioli(ctx, 0, 6, 0, 86, 16, false, g); },
  banana(ctx, g) { ctx.scale(.6, .6); macaroni(ctx, { x: 0, y: 20, a: 0 }, false, g); },
  stone(ctx, g) { ctx.scale(1.3, 1.3); parmesan(ctx, 0, 0, 0, false, g); },
};
type GoalPos = { x: number; y: number; w: number };
const goal = {
  // A plate of spaghetti
  bowl(ctx: Ctx2D, gl: GoalPos, full: number, t: number, g: Gfx) {
    const { x, y, w } = gl;
    ctx.save();
    g.shadowOn(ctx, 16, 8);
    ctx.beginPath();
    ctx.moveTo(x - w / 2 - 10, y - 58); ctx.lineTo(x + w / 2 + 10, y - 58);
    ctx.quadraticCurveTo(x + w / 2 + 2, y + 6, x + w / 2 - 22, y + 8); ctx.lineTo(x - w / 2 + 22, y + 8);
    ctx.quadraticCurveTo(x - w / 2 - 2, y + 6, x - w / 2 - 10, y - 58); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, y - 60, y + 8, '#FFFFFF', '#D6E4F5'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.strokeStyle = '#4DA3FF'; ctx.lineWidth = 5;
    ctx.beginPath(); ctx.moveTo(x - w / 2 - 2, y - 36); ctx.quadraticCurveTo(x, y - 26, x + w / 2 + 2, y - 36); ctx.stroke();
    // nest of spaghetti in the bowl
    ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 10, 10, 0, 0, 7); ctx.fillStyle = '#E8D3A8'; ctx.fill(); ctx.lineWidth = 3; ctx.strokeStyle = INK; ctx.stroke();
    ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 59, w / 2 + 4, 8, 0, 0, 7); ctx.clip();
    ctx.strokeStyle = PASTA2; ctx.lineWidth = 3;
    for (let i = -4; i <= 4; i++) { ctx.beginPath(); ctx.ellipse(x + i * w / 10, y - 60, w / 7, 6, i * .5, 0, 7); ctx.stroke(); }
    ctx.restore();
    // sauce + basil
    ctx.beginPath(); ctx.ellipse(x, y - 64, w * .2, 7, 0, 0, 7); ctx.fillStyle = SAUCE1; ctx.fill();
    ctx.beginPath(); ctx.ellipse(x + w * .12, y - 69, 8, 4, -0.5, 0, 7); ctx.fillStyle = '#4DB85A'; ctx.fill();
    // steam
    ctx.strokeStyle = 'rgba(255,255,255,.85)'; ctx.lineWidth = 4; ctx.lineCap = 'round';
    for (let i = -1; i <= 1; i++) { ctx.beginPath(); const sx = x + i * 26; for (let k = 0; k < 6; k++) ctx.lineTo(sx + Math.sin(t * .05 + k + i) * 5, y - 78 - k * 7); ctx.stroke(); }
    ctx.restore();
  },
  front(ctx: Ctx2D, gl: GoalPos) {
    const { x, y, w } = gl;
    ctx.save(); ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 10, 10, 0, 0, Math.PI); ctx.lineWidth = 6; ctx.strokeStyle = '#FFFFFF'; ctx.stroke();
    ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.beginPath(); ctx.ellipse(x, y - 58, w / 2 + 13, 13, 0, 0.1, Math.PI - 0.1); ctx.stroke(); ctx.restore();
  },
  // A hanging pot lid that says "ding"
  bell(ctx: Ctx2D, gl: { x: number; y: number; hitT?: number }, t: number, g: Gfx) {
    const sw = gl.hitT != null ? Math.sin((t - gl.hitT) * .35) * Math.exp(-(t - gl.hitT) * .03) * .6 : Math.sin(t * .03) * .04;
    ctx.save(); ctx.translate(gl.x, gl.y - 50);
    hook(ctx, 0, 0);
    ctx.rotate(sw);
    ctx.strokeStyle = INK; ctx.lineWidth = 3; ctx.beginPath(); ctx.moveTo(0, 8); ctx.lineTo(0, 18); ctx.stroke();
    g.shadowOn(ctx, 12, 6);
    ctx.beginPath(); ctx.ellipse(0, 22, 9, 6, 0, 0, 7); ctx.fillStyle = '#FF6B4F'; ctx.fill(); g.shadowOff(ctx); ctx.stroke();
    g.shadowOn(ctx, 12, 6);
    ctx.beginPath(); ctx.moveTo(-40, 62); ctx.quadraticCurveTo(-36, 26, 0, 24); ctx.quadraticCurveTo(36, 26, 40, 62); ctx.closePath();
    ctx.fillStyle = g.lg(ctx, 24, 62, '#F4F6FB', '#AEB6C8'); ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 3; ctx.stroke();
    g.rr(ctx, -44, 58, 88, 9, 4); ctx.fillStyle = '#C9CFDC'; ctx.fill(); ctx.stroke();
    ctx.beginPath(); ctx.ellipse(-16, 42, 5, 12, 0.5, 0, 7); ctx.fillStyle = 'rgba(255,255,255,.75)'; ctx.fill();
    ctx.restore();
  },
};

// The meatball (hero ball) and the olive (helper).
// The face is drawn inline: it differs from face() in draw-helpers (eyes look less, ±2 instead of ±2.5, eyes higher and
// further apart, dizzy spiral eyes, mouth and cheeks placed differently).
function meatball(ctx: Ctx2D, b: GameBody, kind: string, t: number, g: Gfx) {
  const { x, y } = b.position, r = b.circleRadius!, v = b.velocity, sp = Math.hypot(v.x, v.y);
  ctx.save(); ctx.translate(x, y);
  if (kind === 'helper') {
    // olive
    g.shadowOn(ctx, 6, 4);
    const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .1, 0, 0, r * 1.1);
    gr.addColorStop(0, '#D5F08A'); gr.addColorStop(.5, '#8FBF3A'); gr.addColorStop(1, '#5C8A22');
    ctx.beginPath(); ctx.ellipse(0, 0, r, r, 0, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
    ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
    ctx.save(); ctx.rotate(b.roll || 0); ctx.beginPath(); ctx.arc(r * .35, -r * .2, r * .3, 0, 7); ctx.fillStyle = '#FF5A5F'; ctx.fill(); ctx.restore();
    ctx.fillStyle = INK; [-1, 1].forEach(s => { ctx.beginPath(); ctx.arc(s * r * .3, -r * .05, r * .13, 0, 7); ctx.fill(); });
    ctx.restore(); return;
  }
  g.shadowOn(ctx, 8, 5);
  const gr = ctx.createRadialGradient(-r * .35, -r * .4, r * .12, 0, 0, r * 1.05);
  gr.addColorStop(0, '#D99A6C'); gr.addColorStop(.5, '#A8603A'); gr.addColorStop(1, '#6E3A22');
  ctx.beginPath(); ctx.arc(0, 0, r, 0, 7); ctx.fillStyle = gr; ctx.fill(); g.shadowOff(ctx);
  ctx.lineWidth = 2.5; ctx.strokeStyle = INK; ctx.stroke();
  // herb specks roll with the ball
  ctx.save(); ctx.rotate(b.roll || 0);
  ctx.fillStyle = 'rgba(70,35,20,.45)';
  [[.55, .3], [-.6, .45], [.1, .7], [-.2, -.75], [.7, -.45]].forEach(([px, py]) => { ctx.beginPath(); ctx.arc(px * r, py * r, r * .1, 0, 7); ctx.fill(); });
  ctx.fillStyle = 'rgba(77,184,90,.9)';
  [[-.45, -.55], [.4, .62]].forEach(([px, py]) => { ctx.beginPath(); ctx.ellipse(px * r, py * r, r * .12, r * .06, px, 0, 7); ctx.fill(); });
  ctx.restore();
  // sauce on top (doesn't turn)
  ctx.beginPath(); ctx.moveTo(-r * .8, -r * .55);
  ctx.quadraticCurveTo(-r * .3, -r * 1.25, r * .8, -r * .6); ctx.quadraticCurveTo(r * .55, -r * .2, r * .35, -r * .45);
  ctx.quadraticCurveTo(r * .2, -r * .1, 0, -r * .5); ctx.quadraticCurveTo(-r * .35, -r * .25, -r * .8, -r * .55); ctx.closePath();
  ctx.fillStyle = b.painted ? '#FF4F3A' : SAUCE1; ctx.fill();
  if (b.painted) { ctx.beginPath(); ctx.arc(0, 0, r - 1, 0.2, Math.PI - 0.2); ctx.lineWidth = 4; ctx.strokeStyle = 'rgba(232,60,40,.7)'; ctx.stroke(); }
  ctx.beginPath(); ctx.ellipse(r * .1, -r * 1.02, r * .25, r * .12, -0.3, 0, 7); ctx.fillStyle = '#4DB85A'; ctx.fill();
  // face
  const lx = Math.max(-2, Math.min(2, v.x * .4)), ly = Math.max(-2, Math.min(2, v.y * .4));
  const scared = v.y > 9, fast = sp > 8.5, ex = r * .36, ey = -r * .02, er = r * .26;
  for (const s of [-1, 1]) {
    ctx.beginPath(); ctx.arc(s * ex, ey, er, 0, 7); ctx.fillStyle = '#fff'; ctx.fill();
    if ((b.dizzy ?? 0) > 0) {
      ctx.strokeStyle = INK; ctx.lineWidth = 1.6; ctx.beginPath();
      for (let i = 0; i < 12; i++) { const tt = i / 12 * Math.PI * 3 + t * .3, q = i / 12 * er * .8; ctx.lineTo(s * ex + Math.cos(tt) * q, ey + Math.sin(tt) * q); }
      ctx.stroke();
    } else { ctx.beginPath(); ctx.arc(s * ex + lx * .6, ey + ly * .6, er * (scared ? .35 : .55), 0, 7); ctx.fillStyle = INK; ctx.fill(); }
  }
  ctx.fillStyle = INK; ctx.strokeStyle = INK; ctx.lineWidth = 2; ctx.lineCap = 'round';
  if (b.sad) { ctx.beginPath(); ctx.arc(0, r * .7, r * .26, 1.2 * Math.PI, 1.8 * Math.PI); ctx.stroke(); }
  else if (fast || scared) { ctx.beginPath(); ctx.ellipse(0, r * .48, r * .16, r * .2, 0, 0, 7); ctx.fill(); }
  else { ctx.beginPath(); ctx.arc(0, r * .3, r * .26, .15 * Math.PI, .85 * Math.PI); ctx.stroke(); }
  ctx.fillStyle = 'rgba(255,120,120,.5)';
  [-1, 1].forEach(s => { ctx.beginPath(); ctx.ellipse(s * r * .62, r * .3, r * .13, r * .08, 0, 0, 7); ctx.fill(); });
  ctx.restore();
}

// ---------- meta ----------
const synth: SynthStyle = { bpm: 72, root: 220, sc: 'minor', prog: [0, 3, 4, 0], bar: 3, lead: 'soft', dens: 0.4 };
const meta: WorldMeta = {
  icon: '🍝', strings, music: 'spaghetti.mp3', synth,
  credits: { title: 'Notte Stellata', artist: 'BFCMUSIC', id: 345499, url: 'https://pixabay.com/music/folk-notte-stellata-italian-accordion-music-345499/' },
};

export default defineWorld({
  k: K,
  meta,
  scene,
  wash: 'rgba(255,248,230,.6)',
  bgExtra(ctx, W, H) {
    // tomato-sauce river at the bottom (don't fall in!)
    ctx.save();
    ctx.beginPath(); ctx.moveTo(0, H - 26);
    for (let x = 0; x <= W; x += 50) ctx.quadraticCurveTo(x + 25, H - 36, x + 50, H - 26);
    ctx.lineTo(W, H); ctx.lineTo(0, H); ctx.closePath();
    ctx.fillStyle = 'rgba(232,86,63,.75)'; ctx.fill();
    ctx.strokeStyle = 'rgba(255,160,130,.8)'; ctx.lineWidth = 4;
    for (let x = 30; x < W; x += 140) { ctx.beginPath(); ctx.moveTo(x, H - 12); ctx.quadraticCurveTo(x + 20, H - 18, x + 40, H - 12); ctx.stroke(); }
    ctx.restore();
  },
  skins,
  ball: meatball,
  goal,
  reskin,
  icons,
  sounds,
  // Stiffer strings and bridges: more constraint/position iterations in this world
  setup(api, level) {
    curLevel = level;
    api.engine.constraintIterations = 6;
    api.engine.positionIterations = 8;
  },
  parts,
  objs,
  levels,
});
